import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { elearningAPI } from '../../services/elearningApi';
import { Module, Enrollment, Course } from '../../types/elearning';
import ModuleSlide from '../../components/elearning/ModuleSlide';
import ModuleSlideNavigation from '../../components/elearning/ModuleSlideNavigation';
import QuizComponent from '../../components/elearning/QuizComponent';
import Breadcrumb from '../../components/layout/Breadcrumb';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import ErrorState from '../../components/ui/ErrorState';
import Button from '../../components/ui/Button';
import { useUser } from '@clerk/clerk-react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { readResume, writeResume } from '../../utils/elearningResume';

export default function ModuleViewer() {
  const { courseSlug, moduleSlug } = useParams<{ courseSlug: string; moduleSlug: string }>();
  const [searchParams] = useSearchParams();
  const { isSignedIn } = useUser();
  const [module, setModule] = useState<Module | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [prerequisitesMet, setPrerequisitesMet] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [, setCompleting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasAutoCompleted, setHasAutoCompleted] = useState(false);
  
  // Slide-by-slide navigation state
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [viewedSlides, setViewedSlides] = useState<Set<string>>(new Set());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const slideRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const appliedSlide = useRef<number | null>(null);
  usePageTitle(module?.title);

  useEffect(() => {
    const fetchData = async () => {
      if (!courseSlug || !moduleSlug) {
        setError(new Error('Invalid course or module slug'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch course first
        const courseData = await elearningAPI.fetchCourse(courseSlug);
        if (!courseData) {
          setError(new Error('Course not found'));
          setLoading(false);
          return;
        }
        setCourse(courseData);
        const userEnrollments = isSignedIn ? await elearningAPI.getUserEnrollments() : [];
        const required = courseData.prerequisites || [];
        const completedIds = new Set(
          userEnrollments
            .filter((item) => item.status === 'COMPLETED')
            .map((item) => item.courseId)
        );
        setPrerequisitesMet(required.every((courseId) => completedIds.has(courseId)));

        // Fetch module
        const moduleData = await elearningAPI.fetchModule(courseSlug, moduleSlug);
        if (!moduleData) {
          setError(new Error('Module not found'));
          setLoading(false);
          return;
        }
        setModule(moduleData);
        
        // Load viewed slides from localStorage (or API in future)
        if (moduleData.content?.slides) {
          const storedViewed = localStorage.getItem(`module_${moduleData.id}_viewed_slides`);
          if (storedViewed) {
            try {
              const viewedIds: unknown = JSON.parse(storedViewed);
              const viewedSet = new Set(
                Array.isArray(viewedIds)
                  ? viewedIds.filter((id): id is string => typeof id === 'string')
                  : []
              );
              setViewedSlides(viewedSet);
              
              // Mark first slide as viewed if no slides viewed yet
              if (viewedSet.size === 0 && moduleData.content.slides.length > 0) {
                const sortedSlides = [...moduleData.content.slides].sort((a, b) => a.order - b.order);
                if (sortedSlides.length > 0) {
                  const updated = new Set([sortedSlides[0].id]);
                  setViewedSlides(updated);
                  localStorage.setItem(`module_${moduleData.id}_viewed_slides`, JSON.stringify(Array.from(updated)));
                }
              }
            } catch (e) {
              console.error('Failed to parse viewed slides:', e);
              // Mark first slide as viewed on error
              if (moduleData.content.slides.length > 0) {
                const sortedSlides = [...moduleData.content.slides].sort((a, b) => a.order - b.order);
                if (sortedSlides.length > 0) {
                  const updated = new Set([sortedSlides[0].id]);
                  setViewedSlides(updated);
                  localStorage.setItem(`module_${moduleData.id}_viewed_slides`, JSON.stringify(Array.from(updated)));
                }
              }
            }
          } else {
            // Mark first slide as viewed if no stored data
            if (moduleData.content.slides.length > 0) {
              const sortedSlides = [...moduleData.content.slides].sort((a, b) => a.order - b.order);
              if (sortedSlides.length > 0) {
                const updated = new Set([sortedSlides[0].id]);
                setViewedSlides(updated);
                localStorage.setItem(`module_${moduleData.id}_viewed_slides`, JSON.stringify(Array.from(updated)));
              }
            }
          }
        }

        // Fetch enrollment if signed in
        if (isSignedIn) {
          const userEnrollment = userEnrollments.find((e) => e.courseId === courseData.id);
          setEnrollment(userEnrollment || null);

          // Check if module is already completed
          if (userEnrollment) {
            const completed = userEnrollment.moduleCompletions?.some(
              (mc) => mc.moduleId === moduleData.id
            );
            setIsCompleted(completed || false);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load module'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseSlug, moduleSlug, isSignedIn]);

  // Track slide views - use useCallback to avoid dependency issues
  const markSlideAsViewed = useCallback((slideId: string, moduleId: string) => {
    setViewedSlides((prev) => {
      const updated = new Set(prev);
      updated.add(slideId);
      
      // Save to localStorage (will be replaced with API call later)
      localStorage.setItem(`module_${moduleId}_viewed_slides`, JSON.stringify(Array.from(updated)));
      
      return updated;
    });
  }, []);

  useEffect(() => {
    appliedSlide.current = null;
  }, [moduleSlug]);

  useEffect(() => {
    if (loading || !module?.content?.slides || module.slug !== moduleSlug || appliedSlide.current !== null) return;
    const slides = [...module.content.slides].sort((a, b) => a.order - b.order);
    const requested = Number(searchParams.get('slide'));
    let index = 0;
    if (Number.isInteger(requested) && requested >= 0 && requested < slides.length) {
      index = requested;
    } else if (enrollment?.userId) {
      const saved = readResume(enrollment.userId, module.courseId);
      if (saved?.moduleSlug === module.slug && saved.slideIndex < slides.length) {
        index = saved.slideIndex;
      }
    }
    appliedSlide.current = index;
    setCurrentSlideIndex(index);
  }, [loading, module, enrollment?.userId, searchParams]);

  useEffect(() => {
    if (!module || module.slug !== moduleSlug || !enrollment?.userId || appliedSlide.current === null) return;
    if (currentSlideIndex !== appliedSlide.current) return;
    writeResume(enrollment.userId, module.courseId, {
      moduleSlug: module.slug,
      slideIndex: currentSlideIndex,
    });
  }, [currentSlideIndex, module, enrollment?.userId]);

  // Handle slide navigation
  const handleSlideSelect = (index: number) => {
    if (!module?.content?.slides) return;
    
    const sortedSlides = [...module.content.slides].sort((a, b) => a.order - b.order);
    if (index >= 0 && index < sortedSlides.length) {
      appliedSlide.current = index;
      setCurrentSlideIndex(index);
      const slide = sortedSlides[index];
      markSlideAsViewed(slide.id, module.id);
      
      // Scroll to top of slide
      const slideElement = slideRefs.current.get(slide.id);
      if (slideElement) {
        slideElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleNextSlide = () => {
    if (!module?.content?.slides) return;
    const sortedSlides = [...module.content.slides].sort((a, b) => a.order - b.order);
    if (currentSlideIndex < sortedSlides.length - 1) {
      handleSlideSelect(currentSlideIndex + 1);
    }
  };

  const handlePreviousSlide = () => {
    if (currentSlideIndex > 0) {
      handleSlideSelect(currentSlideIndex - 1);
    }
  };

  // Track when slide is viewed
  useEffect(() => {
    if (!module?.content?.slides || !module.id) return;
    
    const sortedSlides = [...module.content.slides].sort((a, b) => a.order - b.order);
    const currentSlide = sortedSlides[currentSlideIndex];
    if (currentSlide) {
      markSlideAsViewed(currentSlide.id, module.id);
    }
  }, [currentSlideIndex, module?.id, module?.content?.slides, markSlideAsViewed]);

  const markModuleComplete = useCallback(async () => {
    if (!enrollment || !module) return;
    setCompleting(true);
    try {
      await elearningAPI.completeModule(enrollment.id, module.id);
      setIsCompleted(true);
      if (isSignedIn) {
        await elearningAPI.awardPoints(enrollment.userId, 10, 'Module completion');
        await elearningAPI.updateStreak(enrollment.userId);
      }
    } finally {
      setCompleting(false);
    }
  }, [enrollment, module, isSignedIn]);

  // Modules without a quiz complete when every slide has been opened.
  // A quiz must be passed first; the server rejects completion otherwise.
  useEffect(() => {
    if (!module || !enrollment || isCompleted || hasAutoCompleted || module.hasQuiz || !module.content?.slides) return;

    const sortedSlides = [...module.content.slides].sort((a, b) => a.order - b.order);
    const allSlidesViewed = sortedSlides.length > 0 && viewedSlides.size === sortedSlides.length;
    if (!allSlidesViewed) return;

    const autoComplete = async () => {
      try {
        setHasAutoCompleted(true);
        await markModuleComplete();
      } catch (err) {
        console.error('Failed to auto-complete module:', err);
        setHasAutoCompleted(false);
      }
    };

    autoComplete();
  }, [viewedSlides.size, module, enrollment, isCompleted, hasAutoCompleted, markModuleComplete]);

  const getCurrentModuleIndex = () => {
    if (!course || !module) return -1;
    return course.modules?.findIndex((m) => m.id === module.id) ?? -1;
  };

  const getNextModule = () => {
    if (!course || !module) return null;
    const currentIndex = getCurrentModuleIndex();
    if (currentIndex === -1 || !course.modules) return null;
    return course.modules[currentIndex + 1] || null;
  };

  const getPreviousModule = () => {
    if (!course || !module) return null;
    const currentIndex = getCurrentModuleIndex();
    if (currentIndex === -1 || !course.modules) return null;
    return course.modules[currentIndex - 1] || null;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={10} className="mb-4" />
        <LoadingSkeleton lines={5} />
      </div>
    );
  }

  if (error || !module || !course) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState
          error={error || new Error('Module not found')}
          title="Module Not Found"
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  if (!prerequisitesMet) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to={`/elearning/courses/${course.slug}`}
          className="inline-flex items-center gap-2 text-primary hover:text-primary-dark mb-6 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Back to Course</span>
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mb-3">{module.title}</h1>
        <p className="text-gray-700">Finish the prerequisite courses before starting this module.</p>
      </div>
    );
  }

  const nextModule = getNextModule();
  const previousModule = getPreviousModule();

  // Get sorted slides
  const sortedSlides = module?.content?.slides
    ? [...module.content.slides].sort((a, b) => a.order - b.order)
    : [];
  const currentSlide = sortedSlides[currentSlideIndex];
  const hasNextSlide = currentSlideIndex < sortedSlides.length - 1;
  const hasPreviousSlide = currentSlideIndex > 0;

  return (
    <div className="flex h-[calc(100dvh-6rem)] overflow-hidden bg-gray-50">
      {/* Sidebar Navigation */}
      {module?.content?.slides && sortedSlides.length > 0 && (
        <>
          <div className={`hidden lg:block ${sidebarCollapsed ? 'w-0' : 'w-64'} transition-all duration-300`}>
            <ModuleSlideNavigation
              slides={sortedSlides}
              currentSlideIndex={currentSlideIndex}
              viewedSlides={viewedSlides}
              onSlideSelect={handleSlideSelect}
              isCollapsed={false}
              onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            />
          </div>
          {/* Mobile sidebar overlay */}
          {mobileMenuOpen && (
            <div className="lg:hidden fixed inset-0 z-40">
              <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
              <div className="absolute left-0 top-0 bottom-0 w-64">
                <ModuleSlideNavigation
                  slides={sortedSlides}
                  currentSlideIndex={currentSlideIndex}
                  viewedSlides={viewedSlides}
                  onSlideSelect={(index) => {
                    handleSlideSelect(index);
                    setMobileMenuOpen(false);
                  }}
                  isCollapsed={false}
                  onToggleCollapse={() => setMobileMenuOpen(false)}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between mb-6">
              <Breadcrumb
                items={[
                  { label: 'Home', to: '/' },
                  { label: 'eLearning', to: '/elearning' },
                  { label: course.title, to: `/elearning/courses/${course.slug}` },
                  { label: module.title },
                ]}
              />
              <button
                onClick={() => setMobileMenuOpen((open) => !open)}
                className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                aria-label="Toggle navigation"
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>

            <Link
              to={`/elearning/courses/${course.slug}`}
              className="inline-flex items-center gap-2 text-primary hover:text-primary-dark mb-6 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Back to Course</span>
            </Link>

            {/* Module Header */}
            <div className="mb-8">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{module.title}</h1>
                  {isCompleted && (
                    <div className="flex items-center gap-2 text-green-600 mb-4">
                      <CheckCircleIcon className="h-5 w-5" />
                      <span className="text-sm font-medium">Completed</span>
                    </div>
                  )}
                  {sortedSlides.length > 0 && (
                    <div className="text-sm text-gray-600 mb-4">
                      Slide {currentSlideIndex + 1} of {sortedSlides.length}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Current Slide Content */}
            {currentSlide ? (
              <div
                ref={(el) => {
                  if (el) {
                    slideRefs.current.set(currentSlide.id, el);
                  } else {
                    slideRefs.current.delete(currentSlide.id);
                  }
                }}
                className="mb-8"
              >
                {currentSlide.title && (
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                    {currentSlide.title}
                  </h2>
                )}
                <ModuleSlide slide={currentSlide} />
              </div>
            ) : (
              <div className="mb-8 text-center text-gray-500 py-12">
                <p>No slides available in this module.</p>
              </div>
            )}

            {/* Slide Navigation Buttons */}
            {sortedSlides.length > 1 && (
              <div className="flex items-center justify-between mb-8 pt-6 border-t border-gray-200">
                <Button
                  variant="secondary"
                  onClick={handlePreviousSlide}
                  disabled={!hasPreviousSlide}
                  className="flex items-center gap-2"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                  Previous Slide
                </Button>
                <div className="text-sm text-gray-600">
                  {currentSlideIndex + 1} / {sortedSlides.length}
                </div>
                <Button
                  variant="secondary"
                  onClick={handleNextSlide}
                  disabled={!hasNextSlide}
                  className="flex items-center gap-2"
                >
                  Next Slide
                  <ArrowRightIcon className="h-5 w-5" />
                </Button>
              </div>
            )}

            {/* Quiz stays available before the module is marked complete. */}
            {module.hasQuiz && module.quiz && (sortedSlides.length === 0 || viewedSlides.size === sortedSlides.length) && (
              <div className="mb-8">
                {!isCompleted && (
                  <p className="text-sm text-gray-600 mb-4">
                    Pass the quiz to complete this module.
                  </p>
                )}
                <QuizComponent
                  quiz={module.quiz}
                  enrollmentId={enrollment?.id}
                  onPassed={() => {
                    setIsCompleted(true);
                    if (isSignedIn && enrollment) {
                      void elearningAPI.awardPoints(enrollment.userId, 10, 'Module completion');
                      void elearningAPI.updateStreak(enrollment.userId);
                    }
                  }}
                />
              </div>
            )}

            {enrollment && isCompleted && (
              <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Module Completed!
                    </h3>
                    <p className="text-sm text-gray-600">
                      {module.hasQuiz
                        ? 'You passed the quiz for this module.'
                        : 'You finished the slides in this module.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {enrollment && !isCompleted && sortedSlides.length > 0 && viewedSlides.size < sortedSlides.length && (
              <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">{sortedSlides.length - viewedSlides.size}</span> slide{sortedSlides.length - viewedSlides.size === 1 ? '' : 's'} remaining.
                  {sortedSlides.length - viewedSlides.size === 1 && (
                    module.hasQuiz
                      ? ' View the last slide to open the quiz.'
                      : ' View the last slide to complete this module.'
                  )}
                </p>
              </div>
            )}

            {/* Module Navigation */}
            <div className="flex items-center justify-between pt-8 border-t border-gray-200">
              <div>
                {previousModule ? (
                  <Link
                    to={`/elearning/courses/${course.slug}/modules/${previousModule.slug}`}
                    className="inline-flex items-center gap-2 text-primary hover:text-primary-dark transition-colors"
                  >
                    <ArrowLeftIcon className="h-5 w-5" />
                    <span>Previous Module</span>
                  </Link>
                ) : (
                  <div className="text-gray-400">No previous module</div>
                )}
              </div>
              <div>
                {nextModule ? (
                  <Link
                    to={`/elearning/courses/${course.slug}/modules/${nextModule.slug}`}
                    className="inline-flex items-center gap-2 text-primary hover:text-primary-dark transition-colors"
                  >
                    <span>Next Module</span>
                    <ArrowRightIcon className="h-5 w-5" />
                  </Link>
                ) : (
                  <div className="text-gray-400">No next module</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

